// Files of the computer: dropped on the canvas or pasted, they are uploaded into image (or video) blocks.
// A dropped picture goes where the drop marker shows, or replaces the picture it is dropped on.
import { blockOf } from './registry.js';
import { createBlock } from './parser.js';
import { safeUrl } from './sanitize.js';
import { setVideoSource } from '../blocks/media.js';

function kindOf(file) {
    if (/^image\//.test(file.type)) return 'image';
    if (/^video\//.test(file.type)) return 'video';
    return null;
}

export class FileTransfer {
    constructor(editor) {
        this.editor = editor;
        const doc = editor.canvas.doc;
        this.spot = null;
        doc.addEventListener('dragover', event => this.over(event));
        doc.addEventListener('dragleave', event => {
            if (!event.relatedTarget) this.clear();
        });
        doc.addEventListener('drop', event => this.drop(event));
    }

    hasFiles(event) {
        return !!event.dataTransfer && Array.from(event.dataTransfer.types || []).includes('Files');
    }

    /** The image of the content under the pointer, when a picture dropped there should replace it. */
    imageAt(target) {
        const img = target && target.closest ? target.closest('img') : null;
        const block = img ? img.closest('[data-pf]') : null;
        const def = blockOf(block);
        return img && def && (def.name === 'image' || def.name === 'card') ? { img, block } : null;
    }

    clear() {
        this.spot = null;
        this.editor.overlay.showDrop(null);
    }

    over(event) {
        if (!this.hasFiles(event)) return;
        // Always: otherwise the browser would open the file in the canvas, and the page would be lost.
        event.preventDefault();
        const e = this.editor;
        if (e.preview || !e.media.canUpload('image')) {
            event.dataTransfer.dropEffect = 'none';
            this.clear();
            return;
        }
        event.dataTransfer.dropEffect = 'copy';
        const image = this.imageAt(event.target);
        if (image) {
            this.spot = null;
            e.overlay.showDrop({ parent: image.block, before: null, axis: 'y', moving: null, area: true });
            return;
        }
        this.spot = e.dnd.findSpot(event.clientX, event.clientY, 'image', null);
        e.overlay.showDrop(this.spot);
    }

    async drop(event) {
        if (!this.hasFiles(event)) return;
        event.preventDefault();
        const e = this.editor;
        const spot = this.spot;
        this.clear();
        if (e.preview) return;
        const files = Array.from(event.dataTransfer.files || []).filter(kindOf);
        if (!files.length) {
            e.notify(e.t('error.fileType'), 'error');
            return;
        }
        if (!e.media.canUpload('image')) {
            e.notify(e.t('error.noUpload'), 'warning', 8000);
            return;
        }
        const image = this.imageAt(event.target);
        if (image && files.length === 1 && kindOf(files[0]) === 'image') {
            await this.uploadInto(image.block, image.img, files[0]);
            return;
        }
        await this.insert(files, spot);
    }

    /** Replaces the picture of a block by an uploaded file. */
    async uploadInto(block, img, file) {
        const e = this.editor;
        e.select(block);
        const preview = URL.createObjectURL(file);
        const previous = img.getAttribute('src');
        block.setAttribute('data-pf-uploading', '');
        img.setAttribute('src', preview);
        e.overlay.schedule();
        const result = await e.media.upload(file, 'image');
        block.removeAttribute('data-pf-uploading');
        URL.revokeObjectURL(preview);
        if (!result) {
            img.setAttribute('src', previous || '');
            return;
        }
        img.setAttribute('src', safeUrl(result.url, { image: true }));
        if (!img.getAttribute('alt') && (result.alt || result.label)) img.setAttribute('alt', result.alt || result.label);
        e.commit();
        e.app.inspector.render();
    }

    /** New image or video blocks for files, at a drop spot or near the selection, uploaded one by one. */
    async insert(files, spot = null) {
        const e = this.editor;
        let target = spot || e.insertionSpot('image');
        if (!target) {
            e.notify(e.t('notice.cannotInsert'), 'warning');
            return;
        }
        let added = 0;
        for (const file of files) {
            const kind = kindOf(file);
            if (kind === 'video' && !e.media.canUpload('video')) {
                e.notify(e.t('error.noUpload'), 'warning', 8000);
                continue;
            }
            const fragment = createBlock(kind, e.ctx);
            const block = fragment.firstElementChild;
            target.parent.insertBefore(fragment, target.before);
            block.setAttribute('data-pf-uploading', '');
            const preview = URL.createObjectURL(file);
            if (kind === 'image') block.querySelector('img').setAttribute('src', preview);
            e.select(block);
            e.reveal(block);
            const result = await e.media.upload(file, kind);
            URL.revokeObjectURL(preview);
            if (!block.isConnected) continue; // removed by the user meanwhile
            block.removeAttribute('data-pf-uploading');
            if (!result) {
                if (e.selected === block) e.select(null);
                block.remove();
                continue;
            }
            if (kind === 'image') {
                const img = block.querySelector('img');
                img.setAttribute('src', safeUrl(result.url, { image: true }));
                if (result.alt || result.label) img.setAttribute('alt', result.alt || result.label);
            } else {
                setVideoSource(block, result.url, e.ctx.doc);
            }
            added += 1;
            target = { parent: block.parentNode, before: block.nextElementSibling };
        }
        if (added) {
            e.commit();
            e.app.inspector.render();
        }
    }

    /** Pictures pasted outside a text (a screenshot, a copied picture). Returns true when handled. */
    paste(event) {
        const files = Array.from((event.clipboardData && event.clipboardData.files) || []).filter(file => kindOf(file) === 'image');
        if (!files.length) return false;
        event.preventDefault();
        if (!this.editor.media.canUpload('image')) {
            this.editor.notify(this.editor.t('error.noUpload'), 'warning', 8000);
            return true;
        }
        this.insert(files);
        return true;
    }
}
